import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { redDarkest } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { DOC_TYPE_CONFIG, formatFileSize } from '../flora-document/flora-document.helpers';
import type { PendingAttachment } from './use-agent-attachments.helpers';

/**
 * Read-only chip for an attachment that rode along with a sent user message
 * (filename + file-type icon). Used in the transcript, where there is no upload
 * status or remove control.
 *
 * @param {{ filename: string; docType: string }} props - The attachment's name and doc type
 * @returns {JSX.Element} The read-only attachment chip
 */
export function MessageAttachmentChip({ filename, docType }: { filename: string; docType: string }) {
  const config = DOC_TYPE_CONFIG[docType as keyof typeof DOC_TYPE_CONFIG] ?? DOC_TYPE_CONFIG.docx;
  return (
    <ChipContainer style={{ borderColor: `${config.color}40` }}>
      <IconifyIcon icon={config.icon} width={18} height={18} style={{ flexShrink: 0 }} />
      <ChipContent>
        <FileName size="tiny" weight="medium" color="var(--ink-dark)" title={filename}>
          {filename}
        </FileName>
      </ChipContent>
    </ChipContainer>
  );
}

interface AgentAttachmentChipProps {
  attachment: PendingAttachment;
  onRemove: (localId: string) => void;
}

/**
 * Compact composer chip for a staged file attachment: file-type icon,
 * name, and a status line (size when ready, "Uploading…" while in flight, or the
 * error message on failure), with a remove control. Uploaded docs cannot be
 * PDF-previewed, so this is intentionally a lightweight chip, not a preview.
 *
 * @param {AgentAttachmentChipProps} props - The staged attachment and remove handler
 * @returns {JSX.Element} The attachment chip
 */
export function AgentAttachmentChip({ attachment, onRemove }: AgentAttachmentChipProps) {
  const config = DOC_TYPE_CONFIG[attachment.docType] ?? DOC_TYPE_CONFIG.docx;
  const isError = attachment.status === 'error';
  const isUploading = attachment.status === 'uploading';

  return (
    <ChipContainer style={{ borderColor: isError ? redDarkest : `${config.color}40` }}>
      <IconifyIcon icon={config.icon} width={18} height={18} style={{ flexShrink: 0 }} />
      <ChipContent>
        <FileName size="tiny" weight="medium" color="var(--ink-dark)" title={attachment.filename}>
          {attachment.filename}
        </FileName>
        <Text size="tiny" color={isError ? redDarkest : 'var(--ink-lighter)'}>
          {isError
            ? (attachment.errorMessage ?? 'Upload failed')
            : isUploading
              ? 'Uploading…'
              : formatFileSize(attachment.sizeBytes)}
        </Text>
      </ChipContent>
      {isUploading && <Spinner icon="material-symbols:progress-activity" width={14} height={14} aria-hidden />}
      <RemoveButton
        type="button"
        onClick={() => onRemove(attachment.localId)}
        aria-label={`Remove ${attachment.filename}`}
      >
        <IconifyIcon icon="material-symbols:close" width={12} height={12} />
      </RemoveButton>
    </ChipContainer>
  );
}

const ChipContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 10px;
  border: 1px solid;
  background: var(--base-white);
  max-width: 220px;
`;

const ChipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  line-height: 1.2;
`;

const FileName = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const Spinner = styled(IconifyIcon)`
  flex-shrink: 0;
  color: var(--ink-lighter);
  animation: agent-attachment-spin 0.9s linear infinite;
  @keyframes agent-attachment-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-lighter);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--flora-hover-subtle, rgba(0, 0, 0, 0.05));
    color: var(--ink-dark);
  }
`;
